import PropTypes from 'prop-types';
import * as d3 from 'd3';
import { get, find, isNumber, map, isEmpty } from 'lodash';

import ChartComponent from '../ChartComponent';

import { DEFAULT_SELECTED_ELEMENTS } from '../../../api/Graphics/graphic-operations';

const CARD_MARGIN = 5;
const TOP_MARGIN = 15;
const LEFT_MARGIN = 100;
const BOTTOM_MARGIN = 65;
const RIGHT_MARGIN = 100;
const TEXT_INICIAL_SIZE = 10;
const CHART_CLASS = 'simple-line-chart';

class SimpleLineChart extends ChartComponent {
  id = isEmpty(this.props.atlasID)
    ? `${CHART_CLASS}-card-${this.props.isCard}`
    : this.props.atlasID;

  drawChart = () => {
    const {
      alpha,
      colorRange,
      dataNamed,
      dataNumeric,
      height,
      isCard,
      globalProperties,
      selectedElements,
      weight,
      width
    } = this.props;

    const margin = {
      top: isCard ? CARD_MARGIN : TOP_MARGIN,
      left: isCard ? CARD_MARGIN : LEFT_MARGIN,
      bottom: isCard ? CARD_MARGIN : BOTTOM_MARGIN,
      right: isCard ? CARD_MARGIN : RIGHT_MARGIN
    };

    const svg = d3.select(`#svg-${this.id}`).attr('viewBox', [0, 0, width, height]);

    const data = map(dataNamed, (namedValue, index) => ({
      name: namedValue,
      value: Number(dataNumeric[index])
    }));

    const x = d3
      .scaleBand()
      .domain(map(data, d => d.name))
      .range([margin.left, width - margin.right])
      .padding(0.2);

    const y = d3
      .scaleLinear()
      .domain([
        Math.min(
          0,
          d3.min(data, d => Number(d.value))
        ),
        d3.max(data, d => Number(d.value))
      ])
      .nice()
      .range([height - margin.bottom, margin.top]);

    const line = d3
      .line()
      .curve(d3.curveLinear)
      .defined(d => isNumber(d.value))
      .x(d => x(d.name) + x.bandwidth() / 2)
      .y(d => y(d.value));

    const xAxis = selectedElements.Text
      ? d3 /* eslint-disable indent */
          .axisBottom(x)
          .tickSizeOuter(0) /* eslint-enable indent */
      : d3.axisBottom(x).tickValues([]);

    const yAxis = selectedElements.Text
      ? d3 /* eslint-disable indent */
          .axisLeft(y)
          .ticks() /* eslint-enable indent */
      : d3.axisLeft(y).tickValues([]);

    svg
      .append('path')
      .attr('class', `${this.id}`)
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', colorRange)
      .attr('stroke-opacity', alpha)
      .attr('stroke-width', weight)
      .attr('stroke-linejoin', 'round')
      .attr('stroke-linecap', 'round')
      .attr('d', line);

    if (!isCard) {
      svg
        .append('g')
        .attr('class', `${this.id}`)
        .attr('transform', `translate(0,${y(0)})`)
        .call(xAxis);

      svg
        .selectAll('.tick')
        .selectAll('text')
        .style('text-anchor', 'start')
        .attr(
          'transform',
          `rotate(${get(
            find(globalProperties, ['name', 'xAxisTextAngle']),
            'value',
            TEXT_INICIAL_SIZE
          )})`
        );

      svg
        .append('g')
        .attr('class', `${this.id}`)
        .attr('transform', `translate(${margin.left},0)`)
        .call(yAxis);

      svg
        .selectAll('.tick')
        .selectAll('text')
        .attr(
          'font-size',
          get(find(globalProperties, ['name', 'textSize']), 'value', TEXT_INICIAL_SIZE)
        );

      if (!selectedElements.Axis) {
        svg.selectAll('.domain').remove();
      }
    }
  };
}

SimpleLineChart.defaultProps = {
  width: 0,
  height: 0,
  dataNamed: [0, 1, 2, 3, 4, 4, 6, 7, 8, 9, 10, 11],
  dataNumeric: [1, 1.5, 2, 3, 6, 10, 7, 6.5, 6, 5, 3, 0],
  alpha: 0.9,
  weight: 2,
  colorRange: 'rgba(51, 102, 0, 1)',
  selectedElements: DEFAULT_SELECTED_ELEMENTS,
  globalProperties: [],
  isCard: false
};
SimpleLineChart.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  dataNamed: PropTypes.array,
  dataNumeric: PropTypes.arrayOf(PropTypes.number),
  colorRange: PropTypes.string,
  alpha: PropTypes.number,
  weight: PropTypes.number,
  selectedElements: PropTypes.object,
  globalProperties: PropTypes.array,
  isCard: PropTypes.bool
};
export default SimpleLineChart;
